import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Landmark,
  Cog,
  Users2,
  TrendingUp,
  ClipboardList,
  UtensilsCrossed,
  Sparkles,
} from 'lucide-react';
import { Container, SectionHeading, GlassCard, IconBadge, fadeInUp } from './shared';

const ICONS = [Landmark, Cog, Users2, TrendingUp, ClipboardList];
const CARD_KEYS = ['finance', 'operations', 'hr', 'sales', 'administration'];

const SolutionsSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section id="solutions" className="relative py-24 sm:py-32">
      <Container>
        <SectionHeading eyebrow={t('landing.solutions.eyebrow')} title={t('landing.solutions.title')} />

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CARD_KEYS.map((key, i) => {
            const Icon = ICONS[i];
            return (
              <GlassCard key={key} index={i}>
                <IconBadge>
                  <Icon className="h-6 w-6 text-white" />
                </IconBadge>
                <h3 className="mb-3 text-xl font-semibold text-white">
                  {t(`landing.solutions.cards.${key}.title`)}
                </h3>
                <p className="leading-relaxed text-brand-muted">
                  {t(`landing.solutions.cards.${key}.description`)}
                </p>
              </GlassCard>
            );
          })}

          <motion.div
            id="use-cases"
            variants={fadeInUp}
            custom={5}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            whileHover={{ y: -4 }}
            className="group relative scroll-mt-28 rounded-2xl border border-brand-primary/30 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/5 p-8 backdrop-blur-xl transition-colors duration-300 hover:border-brand-primary/50"
          >
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-secondary">
              <Sparkles className="h-3 w-3" />
              {t('landing.solutions.useCase.badge')}
            </span>
            <IconBadge>
              <UtensilsCrossed className="h-6 w-6 text-white" />
            </IconBadge>
            <h3 className="mb-3 text-xl font-semibold text-white">
              {t('landing.solutions.useCase.title')}
              <span className="block text-sm font-medium text-brand-muted">
                {t('landing.solutions.useCase.subtitle')}
              </span>
            </h3>
            <p className="leading-relaxed text-brand-muted">
              {t('landing.solutions.useCase.description')}
            </p>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default SolutionsSection;
